import type { BannerClosePeriod } from "@/content/dashboardBannerOptions";
import type { DashboardBanner } from "@/content/dashboardBanners";
import { deleteCookie, getCookie, setCookie } from "@/utils/cookies";
import { storedTimes } from "./storedTimes";

const DAY_MS = 24 * 60 * 60 * 1000;

const HIDDEN_FOR_MS: Record<Exclude<BannerClosePeriod, "session">, number> = {
  "1d": DAY_MS,
  "7d": 7 * DAY_MS,
  "30d": 30 * DAY_MS,
  forever: Infinity,
};

/**
 * When each banner was closed, by the banner's id: a banner added in place of
 * a closed one shows, while editing a banner or deciding its experiment
 * keeps its closes, since its id stays.
 */
const timedCloses = storedTimes("dashboardBannerCloses");
/**
 * Banners closed for the session. A cookie with no expiry lasts until the
 * browser closes and holds across tabs, as sessionStorage (one tab) would not.
 */
const SESSION_COOKIE = "dashboardBannerSessionCloses";

const sessionCloses = () =>
  getCookie(SESSION_COOKIE)?.split(",").filter(Boolean) ?? [];

const setSessionCloses = (ids: string[]) => {
  if (ids.length > 0) setCookie(SESSION_COOKIE, ids.join(","));
  else deleteCookie(SESSION_COOKIE);
};

export const rememberClose = (banner: DashboardBanner) => {
  const { id } = banner;
  if (banner.closePeriod === "session") {
    setSessionCloses([...new Set([...sessionCloses(), id])]);
  } else {
    timedCloses.write({ ...timedCloses.read(), [id]: Date.now() });
  }
};

/**
 * Whether a closed banner is still hidden. The period is the banner's current
 * one, so shortening it in Sanity brings back a banner closed under the old one.
 */
export const isClosed = (banner: DashboardBanner) => {
  const { closePeriod } = banner;
  if (!closePeriod) return false;
  if (closePeriod === "session") return sessionCloses().includes(banner.id);
  const closedAt = timedCloses.read()[banner.id];
  return (
    closedAt !== undefined && Date.now() - closedAt < HIDDEN_FOR_MS[closePeriod]
  );
};

/**
 * Forgets closes of banners no longer published, so a finished experiment's
 * closes, "forever" ones included, are not kept for good. A failed read of the
 * banners also answers none, so with none published nothing is forgotten.
 */
export const forgetClosedUnpublishedBanners = (
  published: DashboardBanner[]
) => {
  if (published.length === 0) return;
  const live = new Set(published.map(({ id }) => id));
  timedCloses.keepOnly(live);
  const session = sessionCloses();
  const keptSession = session.filter((id) => live.has(id));
  if (keptSession.length < session.length) setSessionCloses(keptSession);
};
